import { useEffect } from "react";

const DEFAULTS = {
  siteName: "Nova Scotia Security Services",
  base: "https://novascotiaguards.ca",
  keywords: [
    // Core Nova Scotia
    "security services Nova Scotia",
    "security guards Halifax",
    "security company Nova Scotia",
    "licensed security company Nova Scotia",
    "Nova Scotia licensed security guards",
    "24/7 security guards Nova Scotia",
    "Halifax security company",
    "Halifax Regional Municipality security",
    "Cape Breton security company",
    "CBRM security services",
    "Dartmouth security guards",
    "Sydney security services Nova Scotia",
    "Truro security guards",
    "Yarmouth security services",
    "New Glasgow security",
    "Bridgewater security company",

    // Nova Scotia service verticals
    "mobile patrol Nova Scotia",
    "mining security Nova Scotia",
    "event security Halifax",
    "port security Halifax",
    "construction site security Halifax",
    "corporate security Halifax",
    "loss prevention Nova Scotia",
    "concierge security Halifax",
    "executive protection Nova Scotia",
    "fire watch services Nova Scotia",
    "cannabis facility security Nova Scotia",
    "healthcare security Halifax",

    // Canada-wide security industry terms
    "security services Canada",
    "Canadian security company",
    "licensed security guard Canada",
    "Canadian Security Association CANASA",
    "private security industry Canada",
    "provincial security licence Canada",
    "CGSB security guard certification",
    "ASP certified security officer",
    "certified protection officer CPO Canada",
    "security guard training Canada",
    "24/7 mobile patrol Canada",
    "alarm response services Canada",
    "corporate security Canada",
    "industrial security Canada",
    "retail loss prevention Canada",
    "event security Canada",
    "concert security Canada",
    "concierge security Canada",
    "close protection services Canada",
    "executive protection Canada",
    "healthcare security IAHSS Canada",
    "hospital security Canada",
    "fire watch NFPA Canada",
    "NFPA 25 fire watch Canada",
    "cannabis retail security Canada",
    "Health Canada cannabis physical security",
    "port security Canada ISPS",
    "marine security Canada MTSR",
    "mining security Canada",
    "critical infrastructure security Canada",
    "oil and gas security Canada",
    "cash-in-transit security Canada",

    // Atlantic Canada / regional
    "Atlantic Canada security services",
    "Maritimes security company",
    "Maritime security services",
    "Nova Scotia security industry",
    "New Brunswick security services",
    "Prince Edward Island security",
    "Newfoundland and Labrador security guards",
  ],
};

const MANAGED_ATTR = "data-nss-seo";

function upsertMeta(selector, attrs, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    for (const [k, v] of Object.entries(attrs)) {
      if (k !== "tag") el.setAttribute(k, v);
    }
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  if (content != null) {
    if (el.tagName === "LINK") el.setAttribute("href", content);
    else el.setAttribute("content", content);
  }
  return el;
}

function useHead({ title, description, keywords, canonical, image, type, ogTitle, ogDescription }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    upsertMeta('meta[name="description"]', { name: "description" }, description || "");
    upsertMeta('meta[name="keywords"]', { name: "keywords" }, keywords || "");
    upsertMeta(
      'meta[name="robots"]',
      { name: "robots" },
      "index, follow, max-image-preview:large"
    );
    upsertMeta('meta[name="geo.region"]', { name: "geo.region" }, "CA-NS");
    upsertMeta(
      'meta[name="geo.placename"]',
      { name: "geo.placename" },
      "Nova Scotia, Canada"
    );

    upsertMeta(
      'link[rel="canonical"]',
      { tag: "link", rel: "canonical" },
      canonical
    );

    upsertMeta(
      'meta[property="og:site_name"]',
      { property: "og:site_name" },
      DEFAULTS.siteName
    );
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, ogTitle || title);
    upsertMeta(
      'meta[property="og:description"]',
      { property: "og:description" },
      ogDescription || description
    );
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, type || "website");
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    if (image) upsertMeta('meta[property="og:image"]', { property: "og:image" }, image);

    upsertMeta(
      'meta[name="twitter:card"]',
      { name: "twitter:card" },
      "summary_large_image"
    );
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, ogTitle || title);
    upsertMeta(
      'meta[name="twitter:description"]',
      { name: "twitter:description" },
      ogDescription || description
    );
    if (image) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, image);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, keywords, canonical, image, type, ogTitle, ogDescription]);
}

function useJsonLd(jsonLd) {
  useEffect(() => {
    if (!jsonLd) return;
    const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const nodes = list.map((ld) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-nss-jsonld", "true");
      el.textContent = JSON.stringify(ld);
      document.head.appendChild(el);
      return el;
    });
    return () => {
      nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [jsonLd]);
}

export default function SEO({
  title,
  description,
  keywords = [],
  path = "/",
  image,
  type = "website",
  jsonLd,
}) {
  const alreadyBranded =
    title && /nova scotia security services|ns security services/i.test(title);
  const fullTitle = title
    ? alreadyBranded
      ? title
      : `${title} · ${DEFAULTS.siteName}`
    : `${DEFAULTS.siteName} · Security Guard Services Across Nova Scotia`;
  const canonical = `${DEFAULTS.base}${path}`;
  const kw = [...new Set([...DEFAULTS.keywords, ...keywords])].join(", ");

  useHead({
    title: fullTitle,
    description,
    keywords: kw,
    canonical,
    image,
    type,
  });

  useJsonLd(jsonLd);

  return null;
}

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "SecurityService",
  name: "Nova Scotia Security Services",
  description:
    "Licensed security guard services across Nova Scotia — mining, marine, corporate, event, patrol, residential, and executive protection.",
  areaServed: {
    "@type": "AdministrativeArea",
    name: "Nova Scotia",
  },
  address: {
    "@type": "PostalAddress",
    addressRegion: "NS",
    addressCountry: "CA",
  },
  url: "https://novascotiaguards.ca",
};

export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://novascotiaguards.ca/#business",
  name: "Nova Scotia Security Services",
  image:
    "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
  url: "https://novascotiaguards.ca",
  priceRange: "$$-$$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Barrington Street",
    addressLocality: "Halifax",
    addressRegion: "NS",
    postalCode: "B3J",
    addressCountry: "CA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 44.6488,
    longitude: -63.5752,
  },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Nova Scotia" },
    { "@type": "City", name: "Halifax" },
    { "@type": "City", name: "Dartmouth" },
    { "@type": "City", name: "Sydney" },
    { "@type": "City", name: "Truro" },
    { "@type": "City", name: "Yarmouth" },
    { "@type": "City", name: "Bridgewater" },
    { "@type": "City", name: "New Glasgow" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday", "Tuesday", "Wednesday", "Thursday",
      "Friday", "Saturday", "Sunday",
    ],
    opens: "00:00",
    closes: "23:59",
  },
  sameAs: [],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Security Services",
    itemListElement: [
      "Mining Security", "Corporate Security", "Retail Loss Prevention",
      "Event Security", "Mobile Patrol", "Construction Site Security",
      "Residential Security", "Concierge", "Executive Protection",
      "Marine and Port Security", "Industrial Security", "Cannabis Facility Security",
      "Healthcare Security", "Hotel Security", "Fire Watch Services",
    ].map((name) => ({ "@type": "OfferCatalog", name })),
  },
};
